import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiMaximize2, FiMinimize2, FiX } from 'react-icons/fi';
import { useColorContext } from '../../../context/colorContext';
import { useUserContext } from '../../../context/userContext';
import { useLanguageContext } from '../../../context/languageContext';
import MathContentLayer from '../../ui/math/MathContentLayer';
import { AIChatInputBox, chatInputContainsLatex } from './AIchatElements';

const toSafeHex = (hex, fallback) => {
	const value = String(hex || '').trim();
	return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#000000') => {
	const safeHex = toSafeHex(hex, fallback);
	const r = parseInt(safeHex.slice(1, 3), 16);
	const g = parseInt(safeHex.slice(3, 5), 16);
	const b = parseInt(safeHex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const normalizeText = (value) => String(value ?? '').trim();

const stripAssistantSourceMarkers = (value) => {
	const raw = String(value ?? '');
	if (!raw) return '';

	return raw
		// Removes markers like [[S3]], [[ s12 ]], etc.
		.replace(/\[\[\s*S\d+\s*\]\]/gi, '')
		// Removes markers like [source:S3]
		.replace(/\[\s*source\s*:\s*S\d+\s*\]/gi, '')
		// Removes unresolved formula index markers like [[FINDEX:0]] or [FINDEX:0]
		.replace(/\[\[\s*FINDEX\s*:\s*[^\]]+\]\]/gi, '')
		.replace(/\[\s*FINDEX\s*:\s*[^\]]+\]/gi, '')
		// Removes generic unresolved formula placeholders like [[F0]] or [[F_12]]
		.replace(/\[\[\s*F[_A-Z0-9:-]*\d+\s*\]\]/gi, '')
		// Cleanup spacing/line breaks after marker removal.
		.replace(/[ \t]{2,}/g, ' ')
		.replace(/[ \t]+\n/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.replace(/\s+([,.;:!?])/g, '$1')
		.trim();
};

const getUserDisplayName = (user = {}) => {
	const fullName = normalizeText(user?.name);
	if (fullName) return fullName;

	const firstName = normalizeText(user?.first_name);
	const lastName = normalizeText(user?.last_name);
	const combined = normalizeText(`${firstName} ${lastName}`);
	if (combined) return combined;

	const username = normalizeText(user?.username);
	if (username) return username;

	const email = normalizeText(user?.email);
	if (email && email.includes('@')) {
		return normalizeText(email.split('@')[0]);
	}

	return 'there';
};

const inferRole = (entry) => {
	if (!entry || typeof entry !== 'object') return null;

	const roleLike = String(
		entry.role
			?? entry.sender
			?? entry.author
			?? entry.type
			?? ''
	).toLowerCase();

	if (['user', 'human', 'client', 'question'].includes(roleLike)) return 'user';
	if (['assistant', 'ai', 'bot', 'model', 'answer'].includes(roleLike)) return 'assistant';
	if (typeof entry.isUser === 'boolean') return entry.isUser ? 'user' : 'assistant';
	if (typeof entry.fromUser === 'boolean') return entry.fromUser ? 'user' : 'assistant';
	if (typeof entry.question === 'string' && !entry.answer) return 'user';
	if (typeof entry.answer === 'string' && !entry.question) return 'assistant';

	return null;
};

const extractTextFromEntry = (entry) => {
	if (typeof entry === 'string') return entry;
	if (!entry || typeof entry !== 'object') return '';

	if (typeof entry.content === 'string') return entry.content;
	if (typeof entry.message === 'string') return entry.message;
	if (typeof entry.text === 'string') return entry.text;
	if (typeof entry.question === 'string') return entry.question;
	if (typeof entry.answer === 'string') return entry.answer;

	if (Array.isArray(entry.content)) {
		return entry.content
			.map((item) => (typeof item === 'string' ? item : extractTextFromEntry(item)))
			.filter(Boolean)
			.join('\n');
	}

	return '';
};

const getConversationArrayFromInput = (chatConversationJson) => {
	if (Array.isArray(chatConversationJson)) return chatConversationJson;
	if (!chatConversationJson || typeof chatConversationJson !== 'object') return [];

	const candidateKeys = [
		'chat_conversation_json',
		'messages',
		'conversation',
		'chat',
		'history',
		'turns',
	];

	for (const key of candidateKeys) {
		if (Array.isArray(chatConversationJson[key])) {
			return chatConversationJson[key];
		}
	}

	return [];
};

const normalizeMessages = (chatConversationJson) => {
	const rawMessages = getConversationArrayFromInput(chatConversationJson);

	return rawMessages
		.map((entry, index) => {
			if (typeof entry === 'string') {
				const text = normalizeText(entry);
				if (!text) return null;
				return { id: `msg-${index}`, role: 'user', content: text };
			}

			if (!entry || typeof entry !== 'object') return null;

			const role = inferRole(entry);
			const content = normalizeText(extractTextFromEntry(entry));
			if (!content || (role !== 'user' && role !== 'assistant')) return null;

			return {
				id: String(entry.id || entry._id || `msg-${index}`),
				role,
				content,
			};
		})
		.filter(Boolean);
};

const ChatBubble = ({ role, content }) => {
	const { activeAccent, bgPrimary, bgSecondary, textPrimary, textSecondary } = useColorContext();
	const safeAccent = toSafeHex(activeAccent, '#3B82F6');
	const safeBgPrimary = toSafeHex(bgPrimary, '#101114');
	const safeBgSecondary = toSafeHex(bgSecondary, '#161A21');
	const safeTextPrimary = toSafeHex(textPrimary, '#F2F5FA');
	const safeTextSecondary = toSafeHex(textSecondary, '#A8B4C8');
	const isAssistant = role === 'assistant';
	const displayContent = isAssistant ? stripAssistantSourceMarkers(content) : content;
	const shouldUseMathRenderer = isAssistant || chatInputContainsLatex(displayContent);

	return (
		<div
			style={{
				display: 'flex',
				justifyContent: isAssistant ? 'flex-start' : 'flex-end',
			}}
		>
			<div
				style={{
					maxWidth: '88%',
					padding: '11px 13px',
					borderRadius: 14,
					whiteSpace: 'pre-wrap',
					wordBreak: 'break-word',
					fontSize: '0.92rem',
					lineHeight: 1.5,
					background: isAssistant
						? `linear-gradient(145deg, ${hexToRgba(safeBgSecondary, 0.92, safeBgSecondary)} 0%, ${hexToRgba(safeAccent, 0.16, safeAccent)} 100%)`
						: `linear-gradient(145deg, ${hexToRgba(safeAccent, 0.82, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.58, safeAccent)} 100%)`,
					color: safeTextPrimary,
					textShadow: isAssistant ? 'none' : `0 1px 2px ${hexToRgba(safeBgPrimary, 0.35, safeBgPrimary)}`,
					boxShadow: isAssistant
						? `0 12px 22px ${hexToRgba(safeBgPrimary, 0.30, safeBgPrimary)}, 0 0 0 1px ${hexToRgba(safeAccent, 0.10, safeAccent)} inset`
						: `0 12px 22px ${hexToRgba(safeBgPrimary, 0.36, safeBgPrimary)}, 0 0 0 1px ${hexToRgba(safeBgPrimary, 0.08, safeBgPrimary)} inset`,
				}}
			>
				{shouldUseMathRenderer ? (
					<MathContentLayer
						content={displayContent}
						style={{
							whiteSpace: 'pre-wrap',
							wordBreak: 'break-word',
							fontSize: '0.92rem',
							lineHeight: 1.5,
							color: safeTextPrimary,
							textShadow: isAssistant ? 'none' : `0 1px 2px ${hexToRgba(safeBgPrimary, 0.35, safeBgPrimary)}`,
						}}
					/>
				) : (
					displayContent
				)}
			</div>
		</div>
	);
};

const TypingIndicatorBubble = () => {
	const { activeAccent, bgPrimary, bgSecondary, textPrimary } = useColorContext();
	const safeAccent = toSafeHex(activeAccent, '#3B82F6');
	const safeBgPrimary = toSafeHex(bgPrimary, '#101114');
	const safeBgSecondary = toSafeHex(bgSecondary, '#161A21');
	const safeTextPrimary = toSafeHex(textPrimary, '#F2F5FA');

	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'flex-start',
			}}
		>
			<div
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 6,
					maxWidth: '88%',
					padding: '11px 13px',
					borderRadius: 14,
					background: `linear-gradient(145deg, ${hexToRgba(safeBgSecondary, 0.92, safeBgSecondary)} 0%, ${hexToRgba(safeAccent, 0.16, safeAccent)} 100%)`,
					boxShadow: `0 12px 22px ${hexToRgba(safeBgPrimary, 0.30, safeBgPrimary)}, 0 0 0 1px ${hexToRgba(safeAccent, 0.10, safeAccent)} inset`,
				}}
			>
				<span className="ai-chat-typing-dot" style={{ animationDelay: '0ms', background: hexToRgba(safeTextPrimary, 0.88, safeTextPrimary) }} />
				<span className="ai-chat-typing-dot" style={{ animationDelay: '130ms', background: hexToRgba(safeTextPrimary, 0.88, safeTextPrimary) }} />
				<span className="ai-chat-typing-dot" style={{ animationDelay: '260ms', background: hexToRgba(safeTextPrimary, 0.88, safeTextPrimary) }} />
			</div>
		</div>
	);
};

const ChatWelcomeScreen = ({ userName }) => {
	const { textPrimary, textSecondary } = useColorContext();
	const { t } = useLanguageContext();
	const safeTextPrimary = toSafeHex(textPrimary, '#F2F5FA');
	const safeTextSecondary = toSafeHex(textSecondary, '#A8B4C8');

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				textAlign: 'center',
				gap: 8,
				flex: 1,
				minHeight: '100%',
				padding: '12px 8px',
			}}
		>
			<div style={{ color: safeTextPrimary, fontSize: '1.34rem', fontWeight: 800, letterSpacing: '0.01em', lineHeight: 1.25 }}>
				{t('notes.chatWelcomeGreeting', { name: userName })}
			</div>
			<div style={{ color: safeTextSecondary, fontSize: '1.03rem', lineHeight: 1.6, maxWidth: 420 }}>
				{t('notes.chatWelcomeMessage')}
			</div>
		</div>
	);
};

const AIChat = ({
	chatConversationJson,
	onSubmit,
	isSubmitting = false,
	placeholder = 'Ask your question...',
	title = 'Plynote assistant',
	isFullscreen = false,
	onToggleFullscreen,
	onClose,
	style = {},
}) => {
	const { activeAccent, bgPrimary, bgSecondary, textPrimary, textSecondary } = useColorContext();
	const { t } = useLanguageContext();
	const safeAccent = toSafeHex(activeAccent, '#3B82F6');
	const safeBgPrimary = toSafeHex(bgPrimary, '#101114');
	const safeBgSecondary = toSafeHex(bgSecondary, '#161A21');
	const safeTextPrimary = toSafeHex(textPrimary, '#F2F5FA');
	const safeTextSecondary = toSafeHex(textSecondary, '#A8B4C8');

	const [inputValue, setInputValue] = useState('');
	const scrollRef = useRef(null);
	const user = useUserContext();
	const userDisplayName = useMemo(() => getUserDisplayName(user), [user]);

	const messages = useMemo(
		() => normalizeMessages(chatConversationJson),
		[chatConversationJson]
	);

	useEffect(() => {
		if (!scrollRef.current) return;
		scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
	}, [messages.length, isSubmitting]);

	const handleSubmit = async (nextValue) => {
		const text = normalizeText(nextValue);
		if (!text || typeof onSubmit !== 'function' || isSubmitting) return;

		setInputValue('');
		try {
			await onSubmit(text, chatConversationJson);
		} catch (error) {
			setInputValue(text);
			throw error;
		}
	};

	const controlButtonStyle = {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 40,
		height: 40,
		padding: 0,
		borderRadius: 12,
		border: `1px solid ${hexToRgba(safeAccent, 0.58, safeAccent)}`,
		background: `linear-gradient(135deg, ${hexToRgba(safeAccent, 0.34, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.22, safeAccent)} 100%)`,
		color: safeTextPrimary,
		boxShadow: `0 10px 18px ${hexToRgba(safeBgPrimary, 0.26, safeBgPrimary)}, 0 0 0 1px ${hexToRgba(safeAccent, 0.14, safeAccent)} inset`,
		cursor: 'pointer',
		transition: 'transform 180ms ease, filter 180ms ease, box-shadow 180ms ease, background 180ms ease, border-color 180ms ease',
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				width: '100%',
				height: '100%',
				minHeight: 380,
				borderRadius: 16,
				border: `1px solid ${hexToRgba(safeAccent, 0.34, safeAccent)}`,
				background: `linear-gradient(170deg, ${hexToRgba(safeBgSecondary, 0.92, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.96, safeBgPrimary)} 100%)`,
				overflow: 'hidden',
				boxShadow: `0 18px 34px ${hexToRgba(safeBgPrimary, 0.42, safeBgPrimary)}`,
				...style,
			}}
		>
			<style>{`
				@keyframes aiChatTypingPulse {
					0%, 80%, 100% { transform: translateY(0); opacity: 0.32; }
					40% { transform: translateY(-3px); opacity: 1; }
				}
				.ai-chat-typing-dot {
					display: inline-block;
					width: 6px;
					height: 6px;
					border-radius: 999px;
					animation: aiChatTypingPulse 1s infinite ease-in-out;
				}
			`}</style>

			<div
				style={{
					padding: '10px 12px',
					display: 'grid',
					gridTemplateColumns: '1fr auto',
					alignItems: 'center',
					gap: 10,
					background: `linear-gradient(180deg, ${hexToRgba(safeBgSecondary, 0.95, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.86, safeBgPrimary)} 100%)`,
					color: safeTextPrimary,
					fontWeight: 700,
					fontSize: '0.92rem',
					letterSpacing: '0.012em',
				}}
			>
				<div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
				<div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
					{typeof onToggleFullscreen === 'function' ? (
						<button
							type="button"
							onClick={onToggleFullscreen}
							aria-label={isFullscreen ? t('notes.chatExitFullscreen') : t('notes.chatFullscreen')}
							style={controlButtonStyle}
							onMouseEnter={(event) => {
								event.currentTarget.style.transform = 'translateY(-1px)';
								event.currentTarget.style.filter = 'brightness(1.08)';
								event.currentTarget.style.background = `linear-gradient(135deg, ${hexToRgba(safeAccent, 0.42, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.28, safeAccent)} 100%)`;
							}}
							onMouseLeave={(event) => {
								event.currentTarget.style.transform = 'translateY(0)';
								event.currentTarget.style.filter = 'brightness(1)';
								event.currentTarget.style.background = `linear-gradient(135deg, ${hexToRgba(safeAccent, 0.34, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.22, safeAccent)} 100%)`;
							}}
						>
							{isFullscreen ? <FiMinimize2 size={18} /> : <FiMaximize2 size={18} />}
						</button>
					) : null}
					{typeof onClose === 'function' ? (
						<button
							type="button"
							onClick={onClose}
							aria-label={t('notes.closeAssistant')}
							style={controlButtonStyle}
							onMouseEnter={(event) => {
								event.currentTarget.style.transform = 'translateY(-1px)';
								event.currentTarget.style.filter = 'brightness(1.08)';
								event.currentTarget.style.background = `linear-gradient(135deg, ${hexToRgba(safeAccent, 0.42, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.28, safeAccent)} 100%)`;
							}}
							onMouseLeave={(event) => {
								event.currentTarget.style.transform = 'translateY(0)';
								event.currentTarget.style.filter = 'brightness(1)';
								event.currentTarget.style.background = `linear-gradient(135deg, ${hexToRgba(safeAccent, 0.34, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.22, safeAccent)} 100%)`;
							}}
						>
							<FiX size={18} />
						</button>
					) : null}
				</div>
			</div>

			<div
				ref={scrollRef}
				style={{
					flex: 1,
					padding: '14px',
					overflowY: 'auto',
					display: 'flex',
					flexDirection: 'column',
					gap: 10,
					scrollbarWidth: 'thin',
					scrollbarColor: `${hexToRgba(safeAccent, 0.5, safeAccent)} transparent`,
				}}
			>
				{messages.length === 0 ? (
					<>
						<ChatWelcomeScreen userName={userDisplayName} />
						{isSubmitting ? <TypingIndicatorBubble /> : null}
					</>
				) : (
					<>
						{messages.map((message) => (
							<ChatBubble
								key={message.id}
								role={message.role}
								content={message.content}
							/>
						))}
						{isSubmitting ? <TypingIndicatorBubble /> : null}
					</>
				)}
			</div>

			<div
				style={{
					padding: 12,
					background: `linear-gradient(180deg, ${hexToRgba(safeBgPrimary, 0.82, safeBgPrimary)} 0%, ${hexToRgba(safeAccent, 0.1, safeAccent)} 100%)`,
				}}
			>
				<AIChatInputBox
					value={inputValue}
					onChange={setInputValue}
					onSubmit={handleSubmit}
					placeholder={isSubmitting ? t('notes.chatAnswering') : placeholder}
					maxLength={6000}
					style={{
						opacity: isSubmitting ? 0.8 : 1,
						background: `linear-gradient(160deg, ${hexToRgba(safeBgPrimary, 0.94, safeBgPrimary)} 0%, ${hexToRgba(safeAccent, 0.18, safeAccent)} 100%)`,
						border: `2px solid ${hexToRgba(safeAccent, 0.62, safeAccent)}`,
						boxShadow: `0 14px 26px ${hexToRgba(safeBgPrimary, 0.38, safeBgPrimary)}, 0 0 0 1px ${hexToRgba(safeAccent, 0.18, safeAccent)} inset`,
					}}
				/>
			</div>
		</div>
	);
};

export default AIChat;
